import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { callGetMyRoles } from "api/MuyeonApi";
import { memberTypeLabel } from "constants/memberTypes";
import { openNativeOrWeb } from "common/nativeRoute";
import { openRoleVerification } from "common/nativeBridge";
import useBodyScrollLock from "hooks/useBodyScrollLock";

const KEY = "roleVerifyPrompt_dismissed";
const today = () => new Date().toISOString().split("T")[0];

/**
 * 인증 유도 팝업(전역) — 인증 대상 회원유형의 승인이 끝나지 않은 회원에게 안내.
 *  - NONE(미제출): 인증 첨부화면으로 유도. PENDING(심사중): 안내만.
 *  - "오늘 하루 그만보기"로 당일 재노출 방지(localStorage). APPROVED면 미노출.
 *  - 기존 CommonModal 하단시트(modal-bottom) 규칙 재사용.
 */
export default function RoleVerifyPrompt() {
  const { isLoggedIn, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [target, setTarget] = useState(null); // { role, status }
  // 인증 서류 제출 직후(?verifySubmitted=1) 완료 안내 팝업 — 다른 유도 팝업보다 우선.
  const submitted = params.get("verifySubmitted") === "1";
  useBodyScrollLock(!!target || submitted);

  useEffect(() => {
    if (!isLoggedIn) {
      setTarget(null);
      return;
    }
    if (submitted) return; // 방금 제출한 경우엔 완료 팝업만(심사중 유도 중복 방지)
    if (localStorage.getItem(`${KEY}_${today()}`)) return;
    let alive = true;
    callGetMyRoles()
      .then((r) => {
        if (!alive) return;
        const roles = Array.isArray(r?.data?.roles) ? r.data.roles : [];
        const held = Array.isArray(r?.data?.held) ? r.data.held : [];
        const hasGeneralType = held.includes("HOBBY") || r?.data?.activeType === "GENERAL";
        // 일반회원으로 이용 중이면 과거 미완료 인증행 때문에 전역 인증 팝업을 띄우지 않는다.
        // 인증은 회원유형 설정에서 사용자가 직접 시작한다.
        if (hasGeneralType) {
          setTarget(null);
          return;
        }
        // 인증 대상 역할 중 아직 승인 안 된(NONE/PENDING) 것 하나
        const t = roles.find(
          (x) => x.needsVerification && (x.status === "NONE" || x.status === "PENDING"),
        );
        if (t) setTarget({ role: t.role, status: t.status });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [isLoggedIn, submitted]);

  const closeSubmitted = () => {
    const next = new URLSearchParams(params);
    next.delete("verifySubmitted");
    setParams(next, { replace: true });
  };

  // 제출 완료 안내(최우선)
  if (submitted) {
    return (
      <div data-id="modal-bottom" className="modal-bottom-dimmed is-show">
        <div className="modal-bottom">
          <div className="tit-box">
            <p>제출이 완료됐어요</p>
            <button onClick={closeSubmitted} type="button" className="ico-btn">
              <i className="close"></i>
              <span className="visually-hidden"> 닫기 </span>
            </button>
          </div>
          <div className="con-box">
            <p className="t-body">잠시만 기다려 주세요.</p>
            <p className="t-side">심사는 1~2일 안에 끝나요. 승인되면 푸시 알림으로 알려드릴게요.</p>
          </div>
          <div className="btn-group">
            <button type="button" onClick={closeSubmitted} className="btn-md filled primary">확인</button>
          </div>
        </div>
      </div>
    );
  }

  if (!target) return null;

  const label = memberTypeLabel(target.role);
  const isPending = target.status === "PENDING";
  const close = () => setTarget(null);
  const dismissToday = () => {
    localStorage.setItem(`${KEY}_${today()}`, "Y");
    setTarget(null);
  };
  // 서류 첨부는 네이티브 전용 — 웹은 대체 화면이 없어 앱 설치 유도 안내가 뜬다.
  const verify = () => {
    const role = target.role;
    setTarget(null);
    // 학원 인증 화면이 대표원장을 읽기전용으로 보여주도록 실명을 함께 넘긴다.
    openNativeOrWeb(openRoleVerification, { role, ownerName: user?.name || "" });
  };

  return (
    <div data-id="modal-bottom" className="modal-bottom-dimmed is-show">
      <div className="modal-bottom">
        <div className="tit-box">
          <p>{isPending ? "인증 심사 중" : "인증이 필요해요"}</p>
          <button onClick={close} type="button" className="ico-btn">
            <i className="close"></i>
            <span className="visually-hidden"> 닫기 </span>
          </button>
        </div>

        <div className="con-box">
          <p className="t-body">
            {label} {isPending ? "인증을 심사하고 있어요." : "인증이 아직 완료되지 않았어요."}
          </p>
          <p className="t-side">
            {isPending
              ? "승인되면 푸시 알림으로 알려드릴게요."
              : "서류를 첨부해 인증을 완료하면 등록 기능을 사용할 수 있어요."}
          </p>
        </div>

        <div className="btn-group">
          {isPending ? (
            <button type="button" onClick={close} className="btn-md filled primary">확인</button>
          ) : (
            <>
              <button type="button" onClick={close} className="btn-md outlined">나중에</button>
              <button type="button" onClick={verify} className="btn-md filled primary">인증하기</button>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={dismissToday}
          style={{ display: "block", width: "100%", padding: "1.2rem", marginTop: "0.4rem",
                   background: "none", border: "none", color: "#8e8e8e", fontSize: "1.3rem" }}
        >
          오늘 하루 그만보기
        </button>
      </div>
    </div>
  );
}
